import { type ReactNode, useState, useEffect, useMemo } from 'react';
import { useT, RichText } from '../i18n';
import '../styles/onboarding.css';

interface OnboardingStep {
  title: string;
  /** Rich text body using **bold** and _italic_ markers. */
  body: string;
  /** For list items (step 5 scoring) */
  listItems?: string[];
}

export interface OnboardingTooltipProps {
  isOpen: boolean;
  onClose: () => void;
}

const STORAGE_KEY = 'bangos_onboarding_seen';

function useSteps(): OnboardingStep[] {
  const t = useT();
  const nl = '\n\n';
  return useMemo(() => [
    {
      title: t('onboarding.step1Title'),
      body: t('onboarding.step1P1') + nl + t('onboarding.step1P2'),
    },
    {
      title: t('onboarding.step2Title'),
      body: t('onboarding.step2P1') + nl + t('onboarding.step2P2'),
    },
    {
      title: t('onboarding.step3Title'),
      body: t('onboarding.step3P1') + nl + t('onboarding.step3P2') + nl + t('onboarding.step3P3'),
    },
    {
      title: t('onboarding.step4Title'),
      body: t('onboarding.step4P1') + nl + t('onboarding.step4P2'),
    },
    {
      title: t('onboarding.step5Title'),
      body: t('onboarding.step5P1'),
      listItems: [
        t('onboarding.step5Li1'),
        t('onboarding.step5Li2'),
        t('onboarding.step5Li3'),
        t('onboarding.step5Li4'),
      ],
    },
  ], [t]);
}

export function OnboardingTooltip({ isOpen, onClose }: OnboardingTooltipProps): ReactNode {
  const t = useT();
  const STEPS = useSteps();
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to first step when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Mark as seen when dismissed or completed
  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // Safari private browsing mode may throw; silently ignore
    }
    onClose();
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDismiss();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };



  if (!isOpen) return null;

  const step = STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="onboarding-backdrop" onClick={handleDismiss} role="dialog" aria-modal="true">
      <div
        className="onboarding-card"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="onboarding-step-indicator">
          {STEPS.map((_, idx) => (
            <span
              key={idx}
              className={`onboarding-dot ${idx === currentStep ? 'onboarding-dot--active' : ''} ${idx < currentStep ? 'onboarding-dot--done' : ''}`}
            />
          ))}
        </div>

        <h2 className="onboarding-title">{step.title}</h2>
        <div className="onboarding-body">
          {step.body.split('\n\n').filter(Boolean).map((para, i) => (
            <p key={i}><RichText text={para} /></p>
          ))}
          {step.listItems && (
            <ul className="onboarding-scoring-list">
              {step.listItems.map((item, i) => (
                <li key={i}><RichText text={item} /></li>
              ))}
            </ul>
          )}
          {step.title === STEPS[4]?.title && (
            <p><RichText text={t('onboarding.step5P2')} /></p>
          )}
        </div>

        <div className="onboarding-nav">
          <button
            className="onboarding-btn onboarding-btn--skip"
            onClick={handleDismiss}
          >
            {isLastStep ? t('onboarding.gotIt') : t('onboarding.skip')}
          </button>

          <div className="onboarding-nav-right">
            {!isFirstStep && (
              <button
                className="onboarding-btn onboarding-btn--prev"
                onClick={handlePrev}
              >
                {t('onboarding.back')}
              </button>
            )}
            <button
              className="onboarding-btn onboarding-btn--next"
              onClick={handleNext}
            >
              {isLastStep ? t('onboarding.done') : t('onboarding.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Check if the user has completed onboarding before. */
export function hasCompletedOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Safari private browsing mode may throw; treat as not seen
    return false;
  }
}

/** Mark onboarding as seen (for testing or manual reset). */
export function markOnboardingSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Safari private browsing mode may throw; silently ignore
  }
}

/** Reset onboarding status (for testing). */
export function resetOnboardingStatus(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Safari private browsing mode may throw; silently ignore
  }
}
